import React, { useCallback, useState } from "react";
import { Alert, DroppableFileInput } from "@jugl-web/ui-components";
import { useToast, useTranslations } from "@jugl-web/utils";
import { useRestApiProvider } from "@jugl-web/rest-api";
import { useConfirmationDialogState } from "@jugl-web/utils/hooks/useConfirmationDialogState";
import { useAnonymousSession } from "@web-src/modules/auth/providers/AnonymousSessionProvider";
import {
  KnowledgeItem,
  KnowledgeItemType,
} from "@jugl-web/rest-api/ai-workflows/types";
import { useSubscriptionV2 } from "@web-src/modules/cx/providers/SubscriptionV2Provider";
import { AgentKnowledgeContentContainer } from "../AgentKnowledgeContentContainer";
import { ReactComponent as FileIcon } from "./assets/file.svg";
import { ReactComponent as PricingIcon } from "./assets/pricing.svg";
import { FileItem } from "./components/FileItem";
import { useAgentKnowledge } from "../../../../../../hooks/useAgentKnowledge";
import { FileKnowledgePreview } from "./components/FileKnowledgePreview";

export const AgentKnowledgeFilesContent: React.FC<{ entityId: string }> = ({
  entityId,
}) => {
  const { t } = useTranslations();
  const { toast, closeToast } = useToast({ variant: "web" });
  const { aiWorkflowsApi } = useRestApiProvider();
  const {
    pdfKnowledge,
    docxKnowledge,
    handleDeleteKnowledge,
    handleCXApiError,
  } = useAgentKnowledge(entityId);
  const [fileToPreview, setFileToPreview] =
    useState<KnowledgeItem<"pdf" | "docx">>();
  const { isAnonymous } = useAnonymousSession();
  const { isSubscriptionCanceled } = useSubscriptionV2();

  const [uploadFileKnowledge, { isLoading }] =
    aiWorkflowsApi.useUploadFileKnowledgeMutation();

  const isDisabled =
    isAnonymous &&
    (!!pdfKnowledge?.length || !!docxKnowledge?.length || isLoading);

  const deleteConfirmationAlertState = useConfirmationDialogState();

  const handleCreateFileKnowledge = useCallback(
    async (files: File[]) => {
      const uploadingToastId = toast(
        t(
          {
            id: "tasks-details-page.uploading-attachments",
            defaultMessage:
              "Uploading {files} {files, plural, one {File} other {Files}}...",
          },
          {
            files: files.length,
          }
        ),
        { variant: "info", persist: true }
      );

      try {
        await Promise.all(
          files.map((file) =>
            uploadFileKnowledge({
              agentId: entityId,
              entityId,
              type: file.type === "application/pdf" ? "pdf" : "docx",
              file,
            }).unwrap()
          )
        );
      } catch (error) {
        handleCXApiError(error);
      } finally {
        closeToast(uploadingToastId);
      }
    },
    [closeToast, entityId, handleCXApiError, t, toast, uploadFileKnowledge]
  );

  const handleOpenDeleteConfirmation = useCallback(
    (id: string, type: KnowledgeItemType) => {
      deleteConfirmationAlertState.open({
        onConfirm: () => {
          handleDeleteKnowledge(id, type);
          setFileToPreview(undefined);
        },
      });
    },
    [deleteConfirmationAlertState, handleDeleteKnowledge]
  );

  return (
    <>
      {fileToPreview ? (
        <FileKnowledgePreview
          title={fileToPreview.data.ref}
          content={fileToPreview.data.source}
          onRequestClose={() => setFileToPreview(undefined)}
          onDelete={() =>
            handleOpenDeleteConfirmation(
              fileToPreview.data.id,
              fileToPreview.data.type
            )
          }
        />
      ) : (
        <AgentKnowledgeContentContainer
          icon={<FileIcon />}
          title={t({
            id: "cx-ai-agents-page.files",
            defaultMessage: "Files",
          })}
          description={t({
            id: "cx-ai-agents-page.upload-files-to-train-your-ai-agent",
            defaultMessage: "Upload PDF or DOCX files to train your AI Agent",
          })}
        >
          <div className="shrink-0">
            <DroppableFileInput
              isDisabled={isDisabled || isSubscriptionCanceled}
              onUpdate={(files) => {
                if (files?.length) {
                  handleCreateFileKnowledge(files);
                }
              }}
              accept=".docx,.pdf"
              validateType
              multiple={!isAnonymous}
              onIncorrectFile={() => {
                toast(
                  t({
                    id: "cx-ai-agents-page.incorrect-file-format",
                    defaultMessage: "Incorrect file format ❌",
                  }),
                  { variant: "error" }
                );
              }}
              customIcon={isDisabled ? <PricingIcon /> : undefined}
              customContent={
                isDisabled ? (
                  <span className="text-sm">
                    {t({
                      id: "cx-ai-agents-page.signup-to-upload-unlimited-files",
                      defaultMessage:
                        "Signup to upload unlimited number of files",
                    })}
                  </span>
                ) : (
                  <span className="text-sm">
                    {t(
                      {
                        id: "cx-ai-agents-page.browse-or-drag-and-drop-files-here",
                        defaultMessage:
                          "<highlightedText>Browse</highlightedText> or drag and drop files here",
                      },
                      {
                        highlightedText: (text: (string | JSX.Element)[]) => (
                          <span className="text-primary">{text}</span>
                        ),
                      }
                    )}
                  </span>
                )
              }
            />
          </div>
          {(!!pdfKnowledge?.length || !!docxKnowledge?.length) && (
            <div className="mt-8 flex flex-col gap-4">
              <span className="font-secondary font-medium text-black">
                {t({
                  id: "cx-ai-agents-page.added-files",
                  defaultMessage: "Added Files",
                })}
              </span>
              <div className="flex flex-col gap-6">
                {pdfKnowledge?.map((item) => (
                  <FileItem
                    key={item.data.id}
                    type="pdf"
                    title={item.data.ref}
                    size={item.content_size}
                    onDelete={
                      !isSubscriptionCanceled
                        ? () =>
                            handleOpenDeleteConfirmation(
                              item.data.id,
                              item.data.type
                            )
                        : undefined
                    }
                    onClick={() => setFileToPreview(item)}
                  />
                ))}
                {docxKnowledge?.map((item) => (
                  <FileItem
                    key={item.data.id}
                    type="docx"
                    title={item.data.ref}
                    size={item.content_size}
                    onDelete={
                      !isSubscriptionCanceled
                        ? () =>
                            handleOpenDeleteConfirmation(
                              item.data.id,
                              item.data.type
                            )
                        : undefined
                    }
                    onClick={() => setFileToPreview(item)}
                  />
                ))}
              </div>
            </div>
          )}
        </AgentKnowledgeContentContainer>
      )}
      <Alert
        isOpen={deleteConfirmationAlertState.isOpen}
        onRequestClose={deleteConfirmationAlertState.close}
        title={t({
          id: "cx-ai-agents-page.delete-file",
          defaultMessage: "Delete File",
        })}
        content={t({
          id: "cx-ai-agents-page.delete-file-knowledge-description-no-longer-trained",
          defaultMessage:
            "If you will delete selected file, AI Agent will no longer be trained on its info",
        })}
        buttons={[
          {
            text: t({ id: "common.cancel", defaultMessage: "Cancel" }),
            role: "close",
          },
          {
            text: t({ id: "common.delete", defaultMessage: "Delete" }),
            color: "tertiary",
            onClick: () => {
              deleteConfirmationAlertState.confirm({
                closeOnceConfirmed: true,
              });
            },
          },
        ]}
      />
    </>
  );
};
